import React, { useState } from "react";
import { Button } from "@mui/material";
import { useNavigate, useLocation } from "react-router";
import { useTranslation, Trans } from "react-i18next";
import { snackbar } from "tools/events";
import MessageBubbles from "./MessageBubbles";
import HelpfulPrompt from "./HelpfulFeedbackPrompt";
import styled from "styled-components/macro";
import AIFeedbackForm from "./AIFeedbackForm";
import SupportFeedbackModal from "./SupportFeedbackModal";
import { useDBLog } from "hooks/useDBLog";
import { HeaderLayout } from "../../../components/header/Header";
import { useViewerQuery } from "queries/viewerQuery";
import { IconButton } from "@mui/material";
import AddCommentIcon from "@mui/icons-material/AddComment";
import { useLastFeedback } from "./useLastFeedback";

export const SHeaderLayout = styled(HeaderLayout)`
  padding: 20px;
`;

export const RelatedTo = styled.div`
  background: #fffa;
  padding: 5px 12px;
  margin-bottom: 15px;
  border-radius: 5px;
  font-size: 90%;
  color: #777;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const AIWarningMessage = styled.p`
  color: #666;
  font-size: 12px;
  text-align: center;
  margin-top: 10px;
  margin-bottom: 10px;
`;

const Feedback: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { viewer } = useViewerQuery();
  const { last: lastFeedback, clear: clearFeedback, start, markHelpful } = useLastFeedback();
  const [isSupportModalOpen, setIsSupportModalOpen] = useState(false);
  const [isAwaitingAIResponse, setIsAwaitingAIResponse] = useState(false);
  const dbLog = useDBLog();

  const isPublicView = !viewer;

  const { relatedTo, errorMessage, forceDirectSupport } = React.useMemo(() => {
    const searchParams = new URLSearchParams(location.search);

    const relatedTo =
      (location.state && (location.state as any).relatedTo) ||
      (searchParams.get("relatedTo") ? { text: searchParams.get("relatedTo")! } : undefined);

    // error from search params -> prefilling from cordova index if app not loading
    const errorMessage = [
      searchParams.get("error") && `\nError: ${searchParams.get("error")}`,
      searchParams.get("version") && `\nVersion: ${searchParams.get("version")}`,
      searchParams.get("jsVersion") && `\nJS Version: ${searchParams.get("jsVersion")}`
    ]
      .filter(Boolean)
      .join("");

    const forceDirectSupport = searchParams.get("forceDirectSupport") === "true";

    return {
      relatedTo,
      errorMessage,
      forceDirectSupport
    };
  }, [location.state, location.search]);

  const messages = (lastFeedback?.relatedTo || "") === (relatedTo?.text || "") ? lastFeedback?.messages || [] : [];
  const isFeedbackHelpful = lastFeedback?.helpfulAnswer;

  // Clear last feedback if the subject changed (for cases this component is not accessed from Help Center)
  React.useEffect(() => {
    const relatedToChanged = lastFeedback?.relatedTo !== relatedTo?.text;
    if (relatedToChanged && lastFeedback) {
      clearFeedback();
    }
  }, [relatedTo, lastFeedback, clearFeedback]);

  React.useEffect(() => {
    if (forceDirectSupport) {
      setIsSupportModalOpen(true);
    }
  }, [forceDirectSupport]);

  const handleHelpfulResponse = (helpful: boolean) => {
    if (!lastFeedback) return;

    dbLog(`AI Feedback Helped`, helpful ? "yes" : "no", {
      relatedTo: relatedTo?.text,
      feedback: messages[0]?.text
    });

    if (helpful) {
      snackbar(t("Thank you for the feedback!"));
      navigate(-1);
      clearFeedback();
    } else {
      markHelpful(false);
    }
  };

  const assistantMessages = messages.filter((m) => m.owner === "assistant");
  const shouldShowHelpfulPrompt = assistantMessages.length === 1 && isFeedbackHelpful === undefined;
  const shouldShowContactSupportButton =
    assistantMessages.length >= 1 && !isSupportModalOpen && isFeedbackHelpful === false;
  const shouldShowFeedbackForm =
    !isAwaitingAIResponse && (assistantMessages.length !== 1 || isFeedbackHelpful === false);
  const shouldShowAIWarning = assistantMessages.length >= 1;

  return (
    <SHeaderLayout
      title={<Trans>Message</Trans>}
      narrow
      noBack={isPublicView}
      right={
        isFeedbackHelpful === false ? (
          <IconButton onClick={() => start(relatedTo?.text)} size="large">
            <AddCommentIcon />
          </IconButton>
        ) : null
      }
    >
      {relatedTo && <RelatedTo>{relatedTo.text}</RelatedTo>}
      <MessageBubbles showTypingBubble={isAwaitingAIResponse} messages={messages} />

      {shouldShowFeedbackForm && <AIFeedbackForm relatedTo={relatedTo} onSubmitting={setIsAwaitingAIResponse} />}

      {shouldShowAIWarning && (
        <AIWarningMessage>
          <Trans>AI can make mistakes, check important info.</Trans>
        </AIWarningMessage>
      )}

      {shouldShowHelpfulPrompt && (
        <HelpfulPrompt onYes={() => handleHelpfulResponse(true)} onNo={() => handleHelpfulResponse(false)} />
      )}

      {shouldShowContactSupportButton && (
        <Button variant="text" color="primary" fullWidth onClick={() => setIsSupportModalOpen(true)}>
          <Trans>Contact support</Trans>
        </Button>
      )}

      <SupportFeedbackModal
        open={isSupportModalOpen}
        onClose={() => setIsSupportModalOpen(false)}
        relatedTo={relatedTo}
        additionalInfo={errorMessage}
      />
    </SHeaderLayout>
  );
};

export default Feedback;
